import { useMemo, useState } from 'react';
import { Box, Chip, Popover, Stack, TextField, Typography } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickerDay } from '@mui/x-date-pickers/PickerDay';
import dayjs from 'dayjs';

import Icon from '../icon/Icon';

/**
 * MultiDatePicker — adapter MUI per la selezione di N date arbitrarie,
 * sostituto di `react-multi-date-picker` (con plugin `DatePanel`).
 *
 * UX:
 * - Trigger `<TextField>` read-only con label e summary ("3 date selezionate")
 *   o l'elenco compresso delle date (formato DD/MM).
 * - Click → apre `<Popover>` con `<DateCalendar>` MUI a sinistra e una lista
 *   di `<Chip>` removibili (sostituto del `DatePanel`) a destra.
 * - Click su un giorno: toggle (aggiunge se non presente, rimuove se già).
 *
 * Contratto:
 * - `value`: `Array<Date | dayjs | string>` (normalizzato a `dayjs[]` internamente).
 * - `onChange(dates)`: riceve sempre `dayjs[]`.
 * - Locale italiano già montato globalmente via `LocalizationProvider` in `main.jsx`.
 */
export default function MultiDatePicker({
  value = [],
  onChange,
  label,
  format = 'DD/MM/YYYY',
  fullWidth = true,
  size = 'small',
  disabled,
  placeholder = 'Seleziona date',
  minDate,
  maxDate,
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  // Normalizza a `dayjs[]`. Chiavi stabili per dedup e per chip key.
  const selected = useMemo(
    () =>
      (value || []).map((v) => (dayjs.isDayjs(v) ? v : dayjs(v))).filter((d) => d && d.isValid()),
    [value]
  );

  const open = Boolean(anchorEl);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const isSelected = (day) => selected.some((d) => d.isSame(day, 'day'));

  const toggleDay = (day) => {
    const next = isSelected(day)
      ? selected.filter((d) => !d.isSame(day, 'day'))
      : [...selected, day].sort((a, b) => a.valueOf() - b.valueOf());
    onChange?.(next);
  };

  const removeDate = (date) => {
    const next = selected.filter((d) => !d.isSame(date, 'day'));
    onChange?.(next);
  };

  const summary =
    selected.length === 0
      ? ''
      : selected.length <= 2
        ? selected.map((d) => d.format(format)).join(', ')
        : `${selected.length} date selezionate`;

  return (
    <>
      <TextField
        label={label}
        value={summary}
        placeholder={placeholder}
        onClick={disabled ? undefined : handleOpen}
        fullWidth={fullWidth}
        size={size}
        disabled={disabled}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: <Icon name="calendar" style={{ color: 'var(--color-on-surface-muted)' }} />,
            sx: { cursor: disabled ? 'default' : 'pointer' },
          },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ display: 'flex', p: 1, gap: 1 }}>
          <DateCalendar
            views={['year', 'month', 'day']}
            minDate={minDate ? dayjs(minDate) : undefined}
            maxDate={maxDate ? dayjs(maxDate) : undefined}
            // `value` non rappresenta tutta la selezione (DateCalendar è single):
            // glielo passiamo solo per "puntare" al mese visibile.
            value={selected[selected.length - 1] ?? null}
            onChange={(day) => {
              if (day) toggleDay(day);
            }}
            slots={{
              day: (dayProps) => <PickerDay {...dayProps} selected={isSelected(dayProps.day)} />,
            }}
          />
          <Box
            sx={{
              minWidth: 180,
              maxHeight: 320,
              overflowY: 'auto',
              borderLeft: '1px solid',
              borderColor: 'divider',
              pl: 1,
            }}
          >
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
              {selected.length} {selected.length === 1 ? 'data' : 'date'}
            </Typography>
            <Stack spacing={0.5}>
              {selected.map((d) => (
                <Chip
                  key={d.format('YYYY-MM-DD')}
                  label={d.format(format)}
                  size="small"
                  onDelete={() => removeDate(d)}
                />
              ))}
            </Stack>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
